import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Tooltip, Colors } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import type { IconType } from '@cognite/cogs.js';
import { Collapse } from 'antd';

export type ISideBarTab = {
  name: string;
  isActive: boolean;
  sublevels?: ISideBarTab[];
  icon?: IconType;
  onClick: VoidFunction;
  disabled?: boolean;
  tooltip?: string;
};

export const SideBarMenu = ({
  title,
  tabs,
  configurationOnClick,
}: {
  title: string;
  tabs: ISideBarTab[];
  configurationOnClick?: VoidFunction;
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeKey, setActiveKey] = useState('');

  const toggleActiveKey = (k: string | string[] | undefined) => {
    const key = Array.isArray(k) ? k[0] : k || '';
    if (collapsed && key) {
      // unCollapseMenu and expand collapsedTab
      setCollapsed(false);
      setActiveKey(key);
    } else if (key && key === activeKey) {
      // unset currently activeKey
      setActiveKey('');
    } else {
      setActiveKey(key);
    }
  };

  const renderTabHeader = (tab: ISideBarTab) => (
    <Row active={tab.isActive}>
      {tab.icon ? <Icon className="tab-icon" type={tab.icon} /> : null}
      <div className="tab-name">{tab.name}</div>
    </Row>
  );

  const renderSimpleTab = (tab: ISideBarTab) => (
    <Tooltip
      placement="right"
      content={tab.tooltip ? tab.tooltip : tab.name}
      disabled={tab.disabled ? false : !collapsed}
      key={tab.name}
    >
      <SimpleTabItem
        onClick={() => (tab.sublevels && tab.onClick()) || tab.onClick()}
        active={tab.isActive}
        disabled={tab.disabled}
      >
        {renderTabHeader(tab)}
      </SimpleTabItem>
    </Tooltip>
  );

  const renderSimpleSublevelTab = (sublevelTab: ISideBarTab) => (
    <SimpleSublevelTabItem
      key={sublevelTab.name}
      onClick={() => sublevelTab.onClick()}
      active={sublevelTab.isActive}
      disabled={sublevelTab.disabled}
    >
      {renderTabHeader(sublevelTab)}
    </SimpleSublevelTabItem>
  );

  const renderTabWithCollapse = (tab: ISideBarTab) => (
    <Tooltip
      placement="right"
      content={tab.name}
      disabled={!collapsed}
      key={tab.name}
    >
      <StyledCollapse
        key={tab.name}
        active={tab.isActive}
        activeKey={collapsed ? '' : activeKey}
        ghost
        accordion
        destroyInactivePanel
        onChange={toggleActiveKey}
        expandIconPosition="right"
        expandIcon={({ isActive }) => (
          <Icon
            style={{ marginTop: '0.3rem' }}
            type={isActive ? 'ChevronDownSmall' : 'ChevronRightSmall'}
          />
        )}
      >
        <Collapse.Panel
          showArrow={!!tab.sublevels}
          header={renderTabHeader(tab)}
          key={tab.name}
        >
          {tab.sublevels?.map((sublevel) => renderSimpleSublevelTab(sublevel))}
        </Collapse.Panel>
      </StyledCollapse>
    </Tooltip>
  );

  const renderMenuTabs = () =>
    tabs.map((tab) =>
      tab.sublevels ? renderTabWithCollapse(tab) : renderSimpleTab(tab)
    );

  const renderTabWithMenuCollapseControl = () => (
    <Tooltip placement="right" content="Collapse" disabled={!collapsed}>
      <SimpleTabItemCollapse
        onClick={() => setCollapsed((currentState) => !currentState)}
        active={false}
      >
        <Row active={false}>
          <Icon
            className="tab-icon"
            type={collapsed ? 'ArrowRight' : 'ArrowLeft'}
          />
          <div className="tab-name">Collapse</div>
        </Row>
      </SimpleTabItemCollapse>
    </Tooltip>
  );

  const renderConfigurationButton = () => (
    <Tooltip placement="right" content="Configuration" disabled={!collapsed}>
      <SimpleTabItem onClick={configurationOnClick} active={false}>
        <Row active={false}>
          <Icon className="tab-icon" type="Settings" />
          <div className="tab-name">Configuration</div>
        </Row>
      </SimpleTabItem>
    </Tooltip>
  );

  return (
    <StyledSideBarContainer collapsed={collapsed}>
      <div>
        <StyledTitle>
          <span>{title}</span>
        </StyledTitle>
        {renderMenuTabs()}
      </div>
      <div>
        {configurationOnClick && renderConfigurationButton()}
        {renderTabWithMenuCollapseControl()}
      </div>
    </StyledSideBarContainer>
  );
};

const StyledTitle = styled.p`
  color: var(--cogs-greyscale-grey7);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  margin: 1rem;
`;

const StyledSideBarContainer = styled.div<{ collapsed: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: var(--cogs-greyscale-grey2);
  width: 230px;
  justify-content: space-between;
  overflow-y: auto;

  ${(props) =>
    props.collapsed &&
    `
    width: 60px;
    transition: width 150ms cubic-bezier(0.83, 0, 0.17, 1);
    .tab-name {
      display: none;
    }
    ${StyledTitle} span {
      writing-mode: vertical-rl;
      transform: scale(-1);
      text-orientation: mixed;    
      align-self: center;
      height: auto;
      max-height: 65px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;      
    }
  `};
`;

interface ITabItem {
  active: boolean;
  disabled?: boolean;
}

const Row = styled.div<ITabItem>`
  display: flex;
  flex: 1;
  flex-direction: row;

  .tab-name {
    line-height: 20px;
    font-weight: 500;
    margin-left: 0.7rem;
    color: ${(props: ITabItem) =>
      props.active
        ? 'var(--cogs-primary)'
        : 'var(--cogs-text-color-secondary)'};
  }

  .tab-icon {
    margin-top: 0.1rem;
    color: ${(props: ITabItem) =>
      props.active
        ? 'var(--cogs-primary)'
        : 'var(--cogs-text-color-secondary)'};
  }
`;

const SimpleTabItem = styled.button<ITabItem>`
  border: 0;
  padding: 1.2rem;
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;

  transition: all 250ms linear;
  background-color: ${(props: ITabItem) =>
    props.active ? '#fff' : 'transparent'};
  cursor: pointer;
  &:hover {
    background-color: #fcfcfc;
  }
  border-left: solid 4px
    ${(props: ITabItem) => (props.active ? 'var(--cogs-primary)' : 'none')};
  ${(props: ITabItem) =>
    props.disabled &&
    `
    background-color: var(--cogs-greyscale-grey3);
    ${Row} .tab-name, ${Row} .tab-icon {
      color: var(--cogs-greyscale-grey6) !important;
    }
    &:hover {
      background-color: ${Colors['decorative--grayscale--300']};
      cursor: default;
    }
  `};
`;
const SimpleTabItemCollapse = styled(SimpleTabItem)`
  ${Row} {
    align-items: center;
  }
  .tab-icon {
    margin-top: 0.1rem;
    color: var(--cogs-white);
    background-color: var(--cogs-primary);
    border-radius: 100%;
    justify-items: center;
    align-items: center;
    align-self: center;
    padding: 3px;
    width: 20px !important;
    height: 20px;
    > svg {
      object-fit: contain;
    }
  }
`;

const StyledCollapse = styled(Collapse)<ITabItem>`
  width: 100%;
  cursor: pointer;
  align-items: center;
  transition: all 250ms linear;
  background-color: ${(props: ITabItem) => (props.active ? '#fff' : 'none')};

  .ant-collapse-header {
    padding: 1.2rem !important;
    border-left: solid 4px
      ${(props: ITabItem) => (props.active ? 'var(--cogs-primary)' : 'none')};

    .anticon {
      margin-top: 0.4rem;
    }

    .ant-collapse-arrow {
      right: 8px !important;
    }
  }
`;

const SimpleSublevelTabItem = styled.button<ITabItem>`
  padding: 0.8rem;
  width: 100%;
  cursor: pointer;
  transition: all 250ms linear;
  background-color: ${(props: ITabItem) => (props.active ? '#fff' : 'none')};
`;
